/**
 * Per-chat-client localStorage for the chatbot widget.
 *
 * localStorage is shared by every chatbot instance on an origin, so a single
 * unscoped key name let a second chat client on the same domain read the first
 * one's session id and restore its conversation (issue #2503). Every key is
 * therefore suffixed with the chat client's api key, which is globally unique
 * (`Chat.apiKey` is `@Column(length=36, unique=true)`), so the api key alone is
 * a sufficient discriminator and the account is not needed here.
 */

/** Key names used before session ids were scoped per chat client. */
export const LEGACY_KEYS = {
  session: 'omq_chatbot_session_id',
  autoOpen: 'omq_chatbot_auto_open',
  userClosed: 'omq_chatbot_user_closed',
};

/** The localStorage key names belonging to one chat client. */
export interface ChatbotStorageKeys {
  session: string;
  autoOpen: string;
  userClosed: string;
}

/** Product prefix on chat api keys, e.g. `CH-18d72c78-…`. */
const CHAT_API_KEY_PREFIX = 'CH-';

/**
 * Strips the product prefix from a chat api key.
 *
 * The backend resolves `CH-<uuid>` and `<uuid>` to the same chat client
 * (`ChatSec` calls `ApiKeyHelper.removePrefix` before the lookup), so two pages
 * embedding the same widget may spell the key differently. Normalizing keeps
 * both spellings on one storage key — and matching the relay's own
 * normalization keeps the two sides in agreement about session ownership.
 *
 * @param apiKey chat client api key as passed to the widget
 * @returns the api key without its product prefix
 */
const normalizeApiKey = (apiKey: string): string =>
  apiKey.startsWith(CHAT_API_KEY_PREFIX) ? apiKey.slice(CHAT_API_KEY_PREFIX.length) : apiKey;

/**
 * Safe localStorage wrapper that gracefully handles unavailable storage
 * (e.g. private browsing, sandboxed iframes, storage quota exceeded).
 */
export const safeStorage = {
  /**
   * Reads a value, returning null when storage is unavailable.
   *
   * @param key storage key to read
   * @returns the stored value, or null
   */
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  /**
   * Writes a value, silently doing nothing when storage is unavailable.
   *
   * @param key storage key to write
   * @param value value to store
   */
  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // storage unavailable — session won't persist across reloads
    }
  },
  /**
   * Removes a value, silently doing nothing when storage is unavailable.
   *
   * @param key storage key to remove
   */
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // storage unavailable — nothing to remove
    }
  },
};

/**
 * Builds the localStorage key names for one chat client.
 *
 * @param apiKey chat client api key as passed to the widget, with or without prefix
 * @returns the scoped key names for this chat client
 */
export const storageKeysFor = (apiKey: string): ChatbotStorageKeys => {
  const scope = normalizeApiKey(apiKey);
  return {
    session: `${LEGACY_KEYS.session}:${scope}`,
    autoOpen: `${LEGACY_KEYS.autoOpen}:${scope}`,
    userClosed: `${LEGACY_KEYS.userClosed}:${scope}`,
  };
};

/**
 * Reads the session id that would be offered for restore without changing
 * storage. Used at first render to decide whether this page load is a reconnect,
 * which must also be true for a not-yet-migrated legacy entry — otherwise a
 * migrating visitor's window would not re-open on restore.
 *
 * @param apiKey chat client api key
 * @returns the scoped session id, else a legacy session id, else null
 */
export const peekRestoreCandidate = (apiKey: string): string | null =>
  safeStorage.getItem(storageKeysFor(apiKey).session) ?? safeStorage.getItem(LEGACY_KEYS.session);

/**
 * Returns the session id to offer the relay for restore, migrating pre-scoping
 * entries to their scoped names exactly once.
 *
 * A legacy entry carries no api key, so it cannot be attributed to a chat client
 * and is adopted optimistically: if it belongs to a different one the relay
 * rejects the restore and the widget mints a fresh session. `userClosed` moves
 * along with it because dropping it would re-open a widget the visitor had
 * explicitly closed. A legacy entry is left in place when a scoped entry already
 * exists, so another chat client on the domain can still claim it.
 *
 * @param apiKey chat client api key
 * @returns session id to offer for restore, or null when there is none
 */
export const takeRestoreCandidate = (apiKey: string): string | null => {
  const keys = storageKeysFor(apiKey);
  const scoped = safeStorage.getItem(keys.session);
  if (scoped) {
    return scoped;
  }

  const legacySession = safeStorage.getItem(LEGACY_KEYS.session);
  if (!legacySession) {
    return null;
  }

  const legacyAutoOpen = safeStorage.getItem(LEGACY_KEYS.autoOpen);
  const legacyUserClosed = safeStorage.getItem(LEGACY_KEYS.userClosed);

  safeStorage.setItem(keys.session, legacySession);
  if (legacyAutoOpen) {
    safeStorage.setItem(keys.autoOpen, legacyAutoOpen);
  }
  if (legacyUserClosed) {
    safeStorage.setItem(keys.userClosed, legacyUserClosed);
  }

  safeStorage.removeItem(LEGACY_KEYS.session);
  safeStorage.removeItem(LEGACY_KEYS.autoOpen);
  safeStorage.removeItem(LEGACY_KEYS.userClosed);

  return legacySession;
};
